import React, { useState } from "react";
import { BookmarkCheckIcon, BookmarkPlusIcon, LanguagesIcon, LightbulbIcon } from "lucide-react";
import type { BookPage } from "../../types/book";
import { TappableText } from "./TappableText";
import { SpeakButton } from "../games/SpeakButton";
interface BookPageViewProps {
  page: BookPage;
  pageNumber: number;
  speechLang: string;
  activeWord?: string;
  onWord: (word: string, translation: string) => void;
  isSaved: (term: string) => boolean;
  onSaveVocab: (term: string, translation: string) => void;
}
const kindLabel = {
  story: "Story",
  vocab: "Vocabulary",
  dialogue: "Dialogue",
  grammar: "Grammar",
  culture: "Culture"
};
function TranslateToggle({
  translation
}: {
  translation: string;
}) {
  const [open, setOpen] = useState(false);
  return <>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="focus-ring ml-1 inline-flex translate-y-[-2px] items-center rounded-md p-1 align-middle text-ink-2 hover:text-ink" aria-label={open ? "Hide translation" : "Show translation"} data-id="element-1119" data-mp="2mfv7nx8b6jib">
        <LanguagesIcon className="h-4 w-4" data-id="element-1120" data-mp="34yte2qnzx2sn" />
      </button>
      {open && <span className="block text-[15px] italic leading-8 text-ink-2" data-id="element-1121" data-mp="3ixswvhotungw">{translation}</span>}
    </>;
}
export function BookPageView({
  page,
  pageNumber,
  speechLang,
  activeWord,
  onWord,
  isSaved,
  onSaveVocab
}: BookPageViewProps) {
  return <article aria-labelledby={`page-${page.id}`} className="pat-ruled relative rounded-card border-3 border-line bg-surface px-5 pb-10 pt-6 shadow-brutal md:px-10 md:pt-8" data-id="element-1122" data-mp="2q9x5msi11nri">
      <span className="absolute bottom-0 left-8 top-0 hidden w-0.5 bg-bad/30 md:block" aria-hidden="true" data-id="element-1123" data-mp="2zzytpkadr66f" />
      <div className="flex items-center justify-between text-xs font-bold text-ink-2" data-id="element-1124" data-mp="2v7f4fq2bymls">
        <span data-id="element-1125" data-mp="27hkt5cxnt63n">{kindLabel[page.kind]}</span>
        <span data-id="element-1126" data-mp="27gjfr1t687ir">p. {pageNumber}</span>
      </div>
      <h2 id={`page-${page.id}`} className="mt-3 text-[28px] font-bold leading-tight tracking-[-0.02em] text-ink md:text-4xl" lang="es" data-id="element-1127" data-mp="22kb8eal7tim5">
        {page.title}
      </h2>
      {page.subtitle && <p className="text-[15px] leading-8 text-ink-2" data-id="element-1128" data-mp="bey83mqxekf9">{page.subtitle}</p>}

      {page.image && <img src={page.image} alt="Illustration of a busy fruit market stall" className="mt-6 aspect-video w-full rounded-xl border-3 border-line object-cover" data-id="element-1129" data-mp="g5od2yvfwl9f" />}

      {page.paragraphs && <div className="mt-6 space-y-0" data-id="element-1130" data-mp="3jngaisschxf0">
          {page.paragraphs.map((p, i) => <p key={i} className="text-[19px] leading-8 text-ink md:text-xl md:leading-8" lang="es" data-id="element-1131" data-mp="3olrl55qgsbya" data-mpi={i}>
              <TappableText text={p.text} onWord={onWord} activeWord={activeWord} data-id="element-1132" data-mp="3tlckvidgkoc2" />
              <TranslateToggle translation={p.translation} data-id="element-1133" data-mp="3tngvej5v4llb" />
            </p>)}
        </div>}

      {page.dialogue && <ul className="mt-6 space-y-4" data-id="element-1134" data-mp="18vn615cr92jd">
          {page.dialogue.map((line, i) => {
        const right = i % 2 === 1;
        return <li key={i} className={`flex ${right ? "justify-end" : "justify-start"}`} data-id="element-1135" data-mp="1cyju3bl2dqup" data-mpi={i}>
                <div className={`max-w-[85%] rounded-card border-3 border-line px-4 py-3 ${right ? "rounded-br-md bg-brand-tint" : "rounded-bl-md bg-paper"}`} data-id="element-1136" data-mp="2xdpzbf3al6sj">
                  <div className="mb-1 flex items-center justify-between gap-3" data-id="element-1137" data-mp="2jncs5ey85w49">
                    <span className="text-xs font-bold text-ink-2" data-id="element-1138" data-mp="2jla3ay8t6914">{line.speaker}</span>
                    <SpeakButton text={line.text} lang={speechLang} label={`Listen to ${line.speaker}`} data-id="element-1139" data-mp="2elx8nrbp3xnz" />
                  </div>
                  <p className="text-[17px] leading-7 text-ink" lang="es" data-id="element-1140" data-mp="25n1zc6dcu7gg">
                    <TappableText text={line.text} onWord={onWord} activeWord={activeWord} data-id="element-1141" data-mp="25p6fbmoj2wx3" />
                    <TranslateToggle translation={line.translation} data-id="element-1142" data-mp="20qbpi0s5883h" />
                  </p>
                </div>
              </li>;
      })}
        </ul>}

      {page.vocab && <ul className="mt-6 grid gap-2.5 sm:grid-cols-2" data-id="element-1143" data-mp="3v06zqt7bo0ii">
          {page.vocab.map(v => {
        const saved = isSaved(v.term);
        return <li key={v.term} className="flex items-center gap-3 rounded-xl border-3 border-line bg-paper px-3 py-2" data-id="element-1144" data-mp="2wp5pckewt9y3">
                <SpeakButton text={v.term} lang={speechLang} data-id="element-1145" data-mp="2iqeer7z9a6ep" />
                <span className="min-w-0 flex-1" data-id="element-1146" data-mp="2nozel6alio2m">
                  <span className="block truncate text-[16px] font-bold text-ink" lang="es" data-id="element-1147" data-mp="2nrvakp2vv2of">
                    {v.term}
                  </span>
                  <span className="block truncate text-sm text-ink-2" data-id="element-1148" data-mp="2druo4u9pw6g0">{v.translation}</span>
                </span>
                <button type="button" onClick={() => onSaveVocab(v.term, v.translation)} disabled={saved} aria-label={saved ? `${v.term} saved` : `Save ${v.term}`} className={`focus-ring rounded-lg p-1.5 ${saved ? "text-good" : "text-ink-2 hover:text-ink"}`} data-id="element-1149" data-mp="201hiiykat3tu">
                  {saved ? <BookmarkCheckIcon className="h-5 w-5" data-id="element-1150" data-mp="1bes0ve3wygku" /> : <BookmarkPlusIcon className="h-5 w-5" data-id="element-1151" data-mp="1bfjw2ntlmcal" />}
                </button>
              </li>;
      })}
        </ul>}

      {page.grammar && <div className="mt-6" data-id="element-1152" data-mp="1leir33lfy9f4">
          {page.grammarRule && <p className="rounded-xl border-3 border-line bg-brand-tint px-4 py-3 text-[16px] leading-7 text-ink" data-id="element-1153" data-mp="1geo0z29hpnv4">{page.grammarRule}</p>}
          <table className="mt-5 w-full border-separate border-spacing-0 overflow-hidden rounded-xl border-3 border-line text-left" data-id="element-1154" data-mp="1ucl0o7j8qae0">
            <thead data-id="element-1155" data-mp="1pd8d1kkjozad">
              <tr className="bg-ink text-paper" data-id="element-1156" data-mp="244ielm1mvggf">
                <th scope="col" className="px-4 py-2 text-sm font-semibold" data-id="element-1157" data-mp="2437blog0icqd">Form</th>
                <th scope="col" className="px-4 py-2 text-sm font-semibold" data-id="element-1158" data-mp="1z673l7zlqzwm">Example</th>
                <th scope="col" className="hidden px-4 py-2 text-sm font-semibold sm:table-cell" data-id="element-1159" data-mp="2d5oxu2vo35dd">Meaning</th>
              </tr>
            </thead>
            <tbody data-id="element-1160" data-mp="2918sc2pytzyo">
              {page.grammar.map(row => <tr key={row.form} className="bg-surface" data-id="element-1161" data-mp="2g7tfu7h6b3i6">
                  <td className="border-t-2 border-line/15 px-4 py-3 font-bold text-brand" data-id="element-1162" data-mp="2l4tlwie0w69a">{row.form}</td>
                  <td className="border-t-2 border-line/15 px-4 py-3 text-ink" lang="es" data-id="element-1163" data-mp="2q3wjjhroba78">
                    {row.example}
                    <span className="block text-sm text-ink-2 sm:hidden" data-id="element-1164" data-mp="2uvxzbdo1vj20">{row.translation}</span>
                  </td>
                  <td className="hidden border-t-2 border-line/15 px-4 py-3 text-ink-2 sm:table-cell" data-id="element-1165" data-mp="2ywhysnw3iyrb">{row.translation}</td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      {page.note && <p className="mt-6 flex gap-3 rounded-xl border-3 border-line bg-accent px-4 py-3 text-[15px] leading-6 text-night" data-id="element-1166" data-mp="3dtqrbkx4y8km">
          <LightbulbIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" data-id="element-1167" data-mp="1ehy4ui7z4lm5" />
          {page.note}
        </p>}
    </article>;
}